import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Pagination,
  IconButton,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Alert,
  Tooltip,
  Snackbar,
} from "@mui/material";
import { Search, GetApp, ContentCopy } from "@mui/icons-material";
import filterIcon from "../../assets/filterIcon.png";
import { subscriptionService } from "../../services/subscriptionService";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

const Transactions = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copySnackbar, setCopySnackbar] = useState(false);

  // Fetch transactions on component mount
  useEffect(() => {
    fetchTransactions();
  }, []);

  const fetchTransactions = async () => {
    setLoading(true);
    setError(null);

    const result = await subscriptionService.getUserTransactions();

    if (result.success) {
      setTransactions(result.data || []);
    } else {
      setError(result.message);
    }

    setLoading(false);
  };

  // Calculate stats from transaction data
  const calculateStats = () => {
    const totalRevenue = transactions.reduce(
      (sum, t) => sum + (t.total_spent || 0),
      0,
    );
    const totalCredits = transactions.reduce(
      (sum, t) => sum + (t.credits_balance || 0),
      0,
    );
    const avgTransaction =
      transactions.length > 0 ? totalRevenue / transactions.length : 0;

    return {
      totalRevenue: Number(totalRevenue).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
      totalCredits: Number(totalCredits).toLocaleString("en-US"),
      avgTransaction: Number(avgTransaction).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
    };
  };

  const stats = calculateStats();

  // Stats data
  const statsData = [
    {
      title: "Total Revenue",
      value: `$${stats.totalRevenue}`,
      subtitle: "Total spent by all users",
      color: "#E1AD01",
    },
    {
      title: "Total Credits",
      value: stats.totalCredits,
      subtitle: "Combined credits balance",
      color: "#E1AD01",
    },
    {
      title: "Average Transaction",
      value: `$${stats.avgTransaction}`,
      subtitle: "Per user",
      color: "#E1AD01",
    },
  ];

  // Format date helper
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };
  const formatNumber = (number) => {
    return number.toLocaleString("en-US");
  };

  const itemsPerPage = 10;

  // Filter transactions based on search term
  const filteredTransactions = transactions.filter(
    (transaction) =>
      transaction.user_id?.$oid
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      transaction.reason?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      transaction.credits_balance?.toString().includes(searchTerm) ||
      transaction.total_spent?.toString().includes(searchTerm),
  );

  // Calculate total pages based on filtered results so pagination reflects search
  const totalPages = Math.max(
    1,
    Math.ceil(filteredTransactions.length / itemsPerPage),
  );

  // Ensure current page is within range when filtered results change
  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [filteredTransactions.length, totalPages, currentPage]);

  // Paginate filtered transactions
  const paginatedTransactions = filteredTransactions.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setCurrentPage(1); // Reset to first page on search
  };

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  const handleDownloadInvoice = async (transaction) => {
    const userId = transaction.user_id?.$oid;
    if (!userId) return;

    const result = await subscriptionService.getTransactionHistory(userId);
    if (!result.success) {
      alert(result.message || "Failed to fetch transaction history");
      return;
    }

    const transactions = Array.isArray(result.data) ? result.data : [];

    const formatSafeNumber = (value) => {
      return typeof value === "number"
        ? value.toLocaleString("en-US")
        : value || "N/A";
    };
    const coins = result.coins ?? "N/A";

    const doc = new jsPDF();

    // Header
    doc.setFontSize(18);
    doc.setFont("helvetica", "bold");
    doc.text("Transaction History", 14, 20);

    doc.setFontSize(11);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(100);
    doc.text(`User ID: ${userId}`, 14, 28);
    doc.text(`Current Coins: ${formatSafeNumber(coins)}`, 14, 34);
    doc.text(
      `Generated: ${new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}`,
      14,
      40,
    );

    // Table
    const tableData = transactions.map((t, i) => [
      i + 1,
      t.reason || "N/A",
      t.credits_change > 0
        ? `+${formatSafeNumber(t.credits_change)}`
        : `${formatSafeNumber(t.credits_change)}`,
      t.purchase || "-",
      t.created_at?.$date
        ? new Date(t.created_at.$date).toLocaleDateString("en-IN", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          })
        : "N/A",
    ]);

    autoTable(doc, {
      startY: 46,
      head: [["#", "Reason", "Credits Change", "Purchase", "Date"]],
      body: tableData,
      theme: "grid",
      headStyles: {
        fillColor: [225, 173, 1],
        textColor: [0, 0, 0],
        fontStyle: "bold",
      },
      styles: { fontSize: 9, cellPadding: 4 },
      alternateRowStyles: { fillColor: [250, 250, 250] },
      columnStyles: {
        0: { cellWidth: 12, halign: "center" },
        2: { halign: "center" },
        3: { halign: "center" },
      },
    });

    doc.save(`transaction-history-${userId.substring(0, 8)}.pdf`);
  };

  // Loading state
  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          backgroundColor: "#000000",
        }}
      >
        <CircularProgress sx={{ color: "#E1AD01" }} />
      </Box>
    );
  }

  return (
    <Box
      sx={{ padding: "24px", backgroundColor: "#000000", minHeight: "100vh" }}
    >
      {/* Header Section */}
      <Box
        sx={{
          marginBottom: "32px",
        }}
      >
        <Typography
          sx={{
            color: "#FFFFFF",
            fontSize: "32px",
            fontWeight: 600,
            marginBottom: "8px",
          }}
        >
          Transactions
        </Typography>
        <Typography
          sx={{
            color: "#888888",
            fontSize: "16px",
          }}
        >
          View and track earnings, payments, and transaction history.
        </Typography>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ marginBottom: "24px" }}>
          {error}
        </Alert>
      )}

      {/* Stats Cards Section */}
      <Grid container spacing={3} sx={{ marginBottom: "32px" }}>
        {statsData.map((card, index) => (
          <Grid item size={{ xs: 12, sm: 6, md: 4 }} key={index}>
            <Card
              sx={{
                borderRadius: "16px",
                boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)",
                backgroundColor: "#FFFFFF",
                position: "relative",
                overflow: "visible",
              }}
            >
              {/* Yellow accent line */}
              <Box
                sx={{
                  position: "absolute",
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: "8px",
                  height: "100px",
                  my: "auto",
                  backgroundColor: card.color,
                  borderTopRightRadius: "16px",
                  borderBottomRightRadius: "16px",
                }}
              />
              <CardContent sx={{ padding: "24px" }}>
                <Typography
                  variant="h3"
                  sx={{
                    color: "#1A1A1A",
                    fontWeight: 700,
                    fontSize: "36px",
                    marginBottom: 1,
                    lineHeight: 1,
                  }}
                >
                  {card.value}
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    color: "#666666",
                    fontWeight: 500,
                    fontSize: "16px",
                    marginBottom: 1,
                  }}
                >
                  {card.title}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: "#4CAF50",
                    fontSize: "14px",
                    fontWeight: 500,
                  }}
                >
                  {card.subtitle}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Search Section */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          marginBottom: "32px",
          gap: "16px",
        }}
      >
        <TextField
          placeholder="Search Transactions"
          value={searchTerm}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ color: "#888888" }} />
              </InputAdornment>
            ),
          }}
          sx={{
            width: "400px",
            "& .MuiOutlinedInput-root": {
              backgroundColor: "#FFFFFF",
              borderRadius: "25px",
              height: "48px",
              "& fieldset": {
                border: "1px solid #E0E0E0",
              },
              "&:hover fieldset": {
                border: "1px solid #E1AD01",
              },
              "&.Mui-focused fieldset": {
                border: "2px solid #E1AD01",
              },
            },
            "& .MuiOutlinedInput-input": {
              padding: "14px 16px",
              fontSize: "14px",
              color: "#333333",
              "&::placeholder": {
                color: "#666666",
                opacity: 1,
              },
            },
          }}
        />
        <IconButton
          sx={{
            backgroundColor: "#222222",
            color: "#888888",
            "&:hover": {
              backgroundColor: "#333333",
            },
          }}
        >
          <Box
            component="img"
            src={filterIcon}
            alt="filter"
            sx={{ width: 18, height: 18 }}
          />
        </IconButton>
      </Box>

      {/* Transactions Details Section */}
      <Box
        sx={{
          backgroundColor: "#111111",
          borderRadius: "12px",
          padding: "24px",
          marginBottom: "24px",
        }}
      >
        <Typography
          sx={{
            color: "#FFFFFF",
            fontSize: "20px",
            fontWeight: 600,
            marginBottom: "24px",
          }}
        >
          Transactions Details
        </Typography>

        <TableContainer
          component={Paper}
          sx={{
            backgroundColor: "#FFFFFF",
            borderRadius: "12px",
            boxShadow: "none",
          }}
        >
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: "#F8F9FA" }}>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    fontSize: "14px",
                    color: "#333333",
                    padding: "16px",
                  }}
                >
                  User ID
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    fontSize: "14px",
                    color: "#333333",
                    padding: "16px",
                  }}
                >
                  Credits Balance
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    fontSize: "14px",
                    color: "#333333",
                    padding: "16px",
                  }}
                >
                  Total Spent
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    fontSize: "14px",
                    color: "#333333",
                    padding: "16px",
                  }}
                >
                  Reason
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    fontSize: "14px",
                    color: "#333333",
                    padding: "16px",
                  }}
                >
                  Date
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    fontSize: "14px",
                    color: "#333333",
                    padding: "16px",
                  }}
                >
                  Invoice
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedTransactions.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    sx={{
                      textAlign: "center",
                      padding: "32px",
                      color: "#888888",
                    }}
                  >
                    No transactions found
                  </TableCell>
                </TableRow>
              ) : (
                paginatedTransactions.map((transaction, index) => (
                  <TableRow
                    key={transaction._id?.$oid || index}
                    sx={{
                      "&:nth-of-type(odd)": {
                        backgroundColor: "#FAFAFA",
                      },
                      "&:hover": {
                        backgroundColor: "#F5F5F5",
                      },
                    }}
                  >
                    <TableCell
                      sx={{
                        padding: "16px",
                        fontSize: "14px",
                        color: "#333333",
                      }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        <Tooltip
                          title={transaction.user_id?.$oid || "N/A"}
                          arrow
                          placement="top"
                        >
                          <Typography
                            sx={{
                              fontSize: "14px",
                              color: "#333333",
                              cursor: "default",
                            }}
                          >
                            {transaction.user_id?.$oid?.substring(0, 8) ||
                              "N/A"}
                            ...
                          </Typography>
                        </Tooltip>
                        {transaction.user_id?.$oid && (
                          <Tooltip title="Copy User ID" arrow>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigator.clipboard.writeText(
                                  transaction.user_id.$oid,
                                );
                                setCopySnackbar(true);
                              }}
                              sx={{
                                padding: "4px",
                                color: "#999",
                                "&:hover": { color: "#E1AD01" },
                              }}
                            >
                              <ContentCopy sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell
                      sx={{
                        padding: "16px",
                        fontSize: "14px",
                        color: "#333333",
                        fontWeight: 600,
                      }}
                    >
                      {formatNumber(transaction.credits_balance || 0)}
                    </TableCell>
                    <TableCell
                      sx={{
                        padding: "16px",
                        fontSize: "14px",
                        color: "#333333",
                        fontWeight: 600,
                      }}
                    >
                      ${transaction.total_spent || 0}
                    </TableCell>
                    <TableCell
                      sx={{
                        padding: "16px",
                        fontSize: "14px",
                        color: "#333333",
                      }}
                    >
                      <Box
                        sx={{
                          display: "inline-block",
                          padding: "4px 12px",
                          borderRadius: "12px",
                          backgroundColor:
                            transaction.reason?.toLowerCase() === "bonus"
                              ? "#E8F5E9"
                              : transaction.reason?.toLowerCase() === "refund"
                                ? "#FFF3E0"
                                : transaction.reason?.toLowerCase() ===
                                    "onboarding bonus"
                                  ? "#E3F2FD"
                                  : "#F5F5F5",
                          color:
                            transaction.reason?.toLowerCase() === "bonus"
                              ? "#2E7D32"
                              : transaction.reason?.toLowerCase() === "refund"
                                ? "#EF6C00"
                                : transaction.reason?.toLowerCase() ===
                                    "onboarding bonus"
                                  ? "#1976D2"
                                  : "#666666",
                          fontSize: "12px",
                          fontWeight: 500,
                          textTransform: "capitalize",
                        }}
                      >
                        {transaction.reason || "N/A"}
                      </Box>
                    </TableCell>
                    <TableCell
                      sx={{
                        padding: "16px",
                        fontSize: "14px",
                        color: "#333333",
                      }}
                    >
                      {formatDate(transaction.updated_at?.$date)}
                    </TableCell>
                    <TableCell
                      sx={{
                        padding: "16px",
                        fontSize: "14px",
                        color: "#333333",
                      }}
                    >
                      <IconButton
                        onClick={() => handleDownloadInvoice(transaction)}
                        sx={{
                          backgroundColor: "#333333",
                          color: "#FFFFFF",
                          width: "32px",
                          height: "32px",
                          "&:hover": {
                            backgroundColor: "#E1AD01",
                            color: "#000000",
                          },
                        }}
                      >
                        <GetApp sx={{ fontSize: "16px" }} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Pagination - only show when there are multiple pages */}
      {totalPages > 1 && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            marginTop: "24px",
          }}
        >
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={handlePageChange}
            sx={{
              "& .MuiPaginationItem-root": {
                color: "#888888",
                "&.Mui-selected": {
                  backgroundColor: "#E1AD01",
                  color: "#000000",
                },
                "&:hover": {
                  backgroundColor: "#333333",
                },
              },
            }}
          />
        </Box>
      )}
      <Snackbar
        open={copySnackbar}
        autoHideDuration={2000}
        onClose={() => setCopySnackbar(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        sx={{ mt: "80px" }}
      >
        <Alert
          onClose={() => setCopySnackbar(false)}
          severity="success"
          variant="filled"
          sx={{
            width: "100%",
            fontWeight: 500,
            color: "#fff",
            "& .MuiAlert-icon": { color: "#fff" },
            "& .MuiAlert-action .MuiIconButton-root": { color: "#fff" },
          }}
        >
          Copied to clipboard
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Transactions;
